import { useEffect, useRef, useState } from 'react';
import { getFolderSyncState, putFolderSyncState, deleteFolderSyncState, resetFolderSyncDb } from './db';
import { webFileSystemAccessProvider, ScanCancelledError } from './provider';
import { nativeFolderSyncProvider, nativeSyncService, isNativeSyncEnvironment } from './nativeProvider';
import { nativeUploadService } from '../backgroundUpload/nativeUploadService';
import { updateAutoSyncNotification, closeAutoSyncNotification } from './syncNotification';
import { AUTO_SYNC_MAX_BATCH_SIZE } from '../../config/appConfig';
import { findOtherArmedAutoSync, stopAutoSyncRemote } from './autoSyncRegistry';
import { useAuth } from '../../contexts/AuthContext';

// Picks the File System Access API provider in a plain browser PWA, or the
// Storage Access Framework provider when running inside the native Android
// wrapper (visible app or the headless sync-worker WebView alike) - see
// nativeProvider.js's header comment. This is the one seam provider.js's own
// header comment anticipated: everything below this line is unaware of which
// provider is actually in play.
const defaultAutoSyncProvider = isNativeSyncEnvironment() ? nativeFolderSyncProvider : webFileSystemAccessProvider;

const POLL_INTERVAL_MS = 25000;
const DEFAULT_BATCH_SIZE = 100;
const LOCK_BUSY_RETRY_MS = 20000;
const NOTIFICATION_TITLE = 'Circle Moments Auto-Sync';

// `busyRef` (below) only guards against a second submission within the SAME
// useLocalAutoSync hook INSTANCE - it means nothing across a remount. On this
// SPA, navigating away from the event page and back (no app close, no
// backgrounding - just Back-button-to-dashboard-and-return) unmounts and
// remounts this hook fresh: a brand new busyRef defaulting to false, with
// zero memory of the original call's own resolveAndSubmit still running in
// the background. The freshly-mounted instance's own poll tick then sees
// "not busy, pendingQueue still over threshold" and happily starts a SECOND,
// fully independent batch/job for the SAME event while the first is still
// mid-upload - confirmed on a real device as two concurrent jobs (2000 +
// 878 = 2878, both counted into one combined notification total, while the
// in-app UI only ever reflected one of them - "completely unexpected
// behaviour" from the user's perspective, and rightly so). This module-level
// Set (keyed by storageKey) is the fix: it survives exactly this kind of
// remount within the same page session, unlike busyRef.
const activeSubmissions = new Set();

// The whole point of this notification is reassurance that auto-sync is
// still alive and watching in the background - not a live progress readout
// (the actual re-scan is fast now, over well before a human could read a
// counter ticking up). `checking` is shown briefly at the start of each poll
// cycle as a "yes, it's still working" heartbeat; the rest of the time it
// settles on whatever the current pending count actually is. Shared by
// doPoll, resolveAndSubmit and selectFolder so the one persistent
// notification always reflects the true current state regardless of which
// of them last touched it.
function describeSyncStatus({ folderName, pendingCount, batchSize, checking }) {
  if (checking) return `Looking for changes in ${folderName}…`;
  if (pendingCount > 0) {
    // Shows the configured batch threshold alongside the count (e.g. "9/5 pending")
    // so it's clear from the notification alone how close - or how far past - the
    // auto-upload trigger point the folder currently is.
    return `${pendingCount}/${batchSize} pending for upload in ${folderName}`;
  }
  return `Watching ${folderName} - up to date`;
}

// seenKeys used to be stored as `relativePath|size|lastModified` (see git
// history) before change-detection was simplified to relativePath alone -
// without this, a folder that was already armed before that change shipped
// would find every one of its already-synced photos suddenly "new" again on
// its first poll afterwards (old keys never match a plain relativePath) and
// re-upload the whole folder. `|` isn't a legal filename character on any
// platform this runs on, so splitting on the first one safely recovers the
// plain path back out of an old-format key while leaving a already-plain key
// untouched.
function normalizeSeenKey(key) {
  const i = key.indexOf('|');
  return i === -1 ? key : key.slice(0, i);
}

// Orchestrates "watch a local folder, batch new photos, hand batches off to
// the caller" for one event. Provider-agnostic: all filesystem access goes
// through `provider` (defaults to the File System Access API implementation),
// so a future native build can swap in a different provider with the same
// method shapes without touching any of the logic below.
//
// `onBatchReady(fileEntries)` receives an array of `{ file, relativePath }`
// and should upload+kick off (or increment) the grouping job; if it throws,
// the batch is kept in the pending queue for the next retry (auto or manual).
//
// `onReattach()` is called once on mount, only when a batch was still in flight
// (submitted but not yet resolved) when the page was last closed/refreshed -
// e.g. because the caller was still polling grouping-status waiting for an
// EC2-ASG instance to boot. It must check the real server-side job status and:
//   - resolve if that job is confirmed complete (the batch was safely delivered)
//   - throw if it failed or nothing matching is found (so the batch gets requeued)
// Without this, a refresh mid-wait would make the engine "forget" the in-flight
// submission and immediately fire the same files again, colliding with the
// still-running previous job server-side.
// `isExternallyBusy()`, if given, is checked before starting a new submission
// cycle on its own initiative (the poll-tick threshold check, and syncNow) -
// not before onBatchReady itself, which is already serialized via busyRef.
// Lets a caller with its own separate manual-upload pipeline (see
// UploadLinkPage.js) keep the two mutually exclusive end-to-end: without
// this, a manual upload mid-compression wouldn't yet be reflected in this
// hook's own busyRef, so a poll tick crossing the batch-size threshold (or a
// "Sync now" click slipping past a UI that hasn't re-rendered yet) could
// still kick off a second, fully-independent upload+grouping cycle
// concurrently. A deferred cycle just waits for the next poll tick (or a
// fresh syncNow/threshold hit) rather than being dropped.
// `nativeTargetMeta`, if given - `{ uploadLinkToken, identity: {name,email,phone},
// sessionId, canGroup, groupingMode }` - is upload-link-only (see UploadLinkPage.js).
// It's pushed to native's SyncPrefs.Target (via startNativeService below) so
// AutoSyncTrigger.kt can act as this exact guest submitter while the app is
// fully closed, creating a real "upload-link-manual" WorkManager job directly -
// the same native pipeline organiser targets already use, replacing the old
// headless-WebView JS resume (SyncWorkerPage.js's FolderSyncWorker, now
// removed) that used to run this flow instead. Undefined/omitted for the
// organiser flow (EventPage.js never passes this).
export function useLocalAutoSync({ eventId, onBatchReady, onReattach, provider = defaultAutoSyncProvider, storageKey = String(eventId), isExternallyBusy, nativeTargetMeta }) {
  const [state, setState] = useState({
    loaded: false,
    supported: provider.isSupported(),
    armed: false,
    needsReconnect: false,
    folderName: '',
    pendingCount: 0,
    batchSizeSetting: DEFAULT_BATCH_SIZE,
    status: 'idle', // 'idle' | 'watching' | 'submitting' | 'needs-reconnect' | 'error'
    lastError: '',
    // True while a folder listing (initial pick or a background re-poll) is
    // reading the directory - a very large folder (thousands of photos) can
    // take a long time, so the UI should show real progress + a cancel
    // affordance instead of silently appearing to do nothing. See
    // scanProgress for the { phase, completed, total } detail, and cancelScan.
    scanning: false,
    scanProgress: null,
    scanCancelRequested: false,
    // Populated while status === 'submitting' so any UI displaying this hook's state
    // (SmartSortSection's or AddPhotosSection's Step 1/2 area, whichever is on screen)
    // can show the same upload progress the manual flows show - fed via the setLog/
    // setProgress/setStatusComment hooks passed into runOrganiserUploadBatches.
    progress: { processed: 0, total: 0, percent: 0, current_image: '', phase: '' },
    statusComment: '',
    log: '',
  });

  const dirHandleRef = useRef(null);
  const seenKeysRef = useRef(new Set());
  const pendingQueueRef = useRef([]); // [{ relativePath }]
  // Entries that have been handed off to onBatchReady but not yet resolved -
  // tracked (and persisted) separately from pendingQueueRef so a refresh mid-submission
  // can tell "this batch was already sent, go check on it" apart from "this batch was
  // never sent, safe to send now".
  const inFlightBatchRef = useRef([]);
  const batchSizeRef = useRef(DEFAULT_BATCH_SIZE);
  // Mirrors state.needsReconnect for persist() below - autoSyncRegistry.js's
  // getAllArmedAutoSyncs reads this straight from IndexedDB (no live hook
  // instance exists on Landing/Dashboard, where ActiveAutoSyncPill mounts) to
  // avoid showing a "running" pill for an auto-sync that's actually stuck
  // waiting on the user to reconnect.
  const needsReconnectRef = useRef(false);
  const pollTimerRef = useRef(null);
  const lockBusyTimerRef = useRef(null);
  const busyRef = useRef(false);
  // Synchronous re-entrancy guard for folder scans - set at the very top of
  // selectFolder/doPoll, before any await, so a second "Choose folder" click
  // (or a background poll firing while the previous one is still scanning a
  // huge folder) is a plain no-op instead of contending for the same starved
  // browser I/O and eventually firing a second OS directory picker once the
  // first scan's promise chain finally unwinds.
  const scanningRef = useRef(false);
  const scanAbortRef = useRef(null);
  const onBatchReadyRef = useRef(onBatchReady);
  onBatchReadyRef.current = onBatchReady;
  const onReattachRef = useRef(onReattach);
  onReattachRef.current = onReattach;
  const isExternallyBusyRef = useRef(isExternallyBusy);
  isExternallyBusyRef.current = isExternallyBusy;
  const nativeTargetMetaRef = useRef(nativeTargetMeta);
  nativeTargetMetaRef.current = nativeTargetMeta;
  // Populated once stopSync is defined below (assigned every render, same
  // pattern as the other *Ref assignments above) - lets the login/logout
  // effect further down call the CURRENT stopSync closure without adding a
  // fresh-every-render function to that effect's own dependency array.
  const stopSyncRef = useRef(null);
  const { user } = useAuth();

  const persist = (extra = {}) => {
    if (!dirHandleRef.current) return Promise.resolve();
    return putFolderSyncState({
      eventId,
      mode: 'autosync',
      dirHandle: dirHandleRef.current,
      armed: true,
      needsReconnect: needsReconnectRef.current,
      batchSizeSetting: batchSizeRef.current,
      seenKeys: Array.from(seenKeysRef.current),
      pendingQueue: pendingQueueRef.current,
      inFlightBatch: inFlightBatchRef.current,
      ...extra,
    }, storageKey).catch(() => {});
  };

  // Best-effort - if notifications aren't granted/supported, these are no-ops
  // (see syncNotification.js). Never awaited by callers; a slow or failing
  // notification call must never hold up the actual sync work. `signal`, if
  // given, lets a stale call (dispatched before a scan was aborted) recognise
  // that and skip re-displaying the notification after clearNotify() already
  // closed it - see updateAutoSyncNotification's comment.
  const notify = (body, signal) => { updateAutoSyncNotification(storageKey, { title: NOTIFICATION_TITLE, body, signal }); };
  const clearNotify = () => { closeAutoSyncNotification(storageKey); };

  // Keeps FolderSyncForegroundService's lifecycle mirroring this hook's own
  // armed/un-armed state, so folder polling keeps running (and the one
  // persistent stats notification stays up) even after the visible app is
  // fully backgrounded or killed. No-ops on web (nativeSyncService checks
  // Capacitor.isNativePlatform() itself) and safe to call redundantly (e.g.
  // on every mount-reattach) - the native side treats a start call for an
  // already-running storageKey as just refreshing its parameters.
  const startNativeService = () => {
    if (!dirHandleRef.current?.treeUri) return;
    console.log('[AutoSync] startNativeService: storageKey=%s treeUri=%s batchThreshold=%s', storageKey, dirHandleRef.current.treeUri, batchSizeRef.current);
    // Best-effort, fire-and-forget - resolves instantly with no dialog if
    // already exempt (the common case after the first arm), so safe to call
    // on every reattach. See requestBatteryOptimizationExemption's own doc
    // comment for why this alone doesn't guarantee survival on every OEM.
    nativeSyncService.requestBatteryOptimizationExemption().catch(() => {});
    const meta = nativeTargetMetaRef.current;
    nativeSyncService.startForegroundSync({
      storageKey,
      treeUri: dirHandleRef.current.treeUri,
      // Capacitor's PluginCall.getString("eventId") on the native side comes back
      // null when the bridge call carries a plain JS number (event.event_id is
      // numeric, unlike storageKey which the hook's own default already stringifies
      // via String(eventId)) - confirmed on-device via logcat as
      // AutoSyncCheckWorker.checkTarget silently skipping every check forever
      // ("storageKey=49 has no eventId, skipping"), permanently disabling the
      // closed-app auto-sync fallback for every organiser target. Stringified here,
      // at the one call site that sends it across the bridge, rather than changing
      // the hook's own eventId prop type everywhere else it's used.
      eventId: eventId == null ? eventId : String(eventId),
      batchThreshold: batchSizeRef.current,
      // Guest/upload-link fields (see nativeTargetMeta's own doc comment above) -
      // all simply undefined for the organiser flow, which never passes this.
      uploadLinkToken: meta?.uploadLinkToken,
      identityName: meta?.identity?.name,
      identityEmail: meta?.identity?.email,
      identityPhone: meta?.identity?.phone,
      sessionId: meta?.sessionId,
      canGroup: meta?.canGroup,
      groupingMode: meta?.groupingMode,
    });
  };
  const stopNativeService = () => {
    console.log('[AutoSync] stopNativeService: storageKey=%s', storageKey);
    nativeSyncService.stopForegroundSync(storageKey);
  };
  // Reports the outcome of a batch (successful or failed) so the foreground
  // service's persistent notification reflects reality regardless of which
  // context actually drove this resolveAndSubmit call - the visible app's own
  // 25s poll, or a headless batch the service itself woke (see
  // FolderSyncForegroundService/SyncWorkerPage.js).
  //
  // `statusDetail`, if given, overrides the notification's usual "N/M pending"
  // text with a free-form string (e.g. "3 / 6 photos compressed") for as long
  // as it's non-null - always pass it explicitly (even as null/undefined) so a
  // stale detail from an earlier call doesn't linger once this one supersedes
  // it. This is the ONLY notification path that actually reaches the user on
  // native Android: syncNotification.js's notify()/updateAutoSyncNotification
  // is deliberately a no-op whenever Capacitor.isNativePlatform() is true (see
  // its own isSupported() comment - FolderSyncForegroundService owns that
  // notification instead), and in the headless WebView specifically it hangs
  // forever awaiting navigator.serviceWorker.ready, since no service worker
  // ever successfully registers there (confirmed on a real device via a
  // logged "Error during service worker registration" failure) - so a plain
  // notify() call, while harmless (never awaited), never actually shows
  // anything on this platform either way.
  const reportNativeBatchResult = (lastResult, statusDetail = null) => {
    nativeSyncService.reportBatchResult({
      storageKey,
      pendingCount: pendingQueueRef.current.length,
      lastResult,
      lastSyncAt: Date.now(),
      statusDetail,
    });
  };

  const resolveAndSubmit = async (entriesToSubmit) => {
    // activeSubmissions (module-level, see its own doc comment above) catches
    // exactly what busyRef alone can't: a second, independent hook instance
    // (from a remount) trying to submit for the same storageKey while an
    // earlier instance's own submission is still genuinely in flight.
    if (!entriesToSubmit.length || busyRef.current || activeSubmissions.has(storageKey)) {
      // This bail was previously silent - no state change, no notification, no
      // log - which made a real occurrence indistinguishable from "resolveAndSubmit
      // was never called at all" when diagnosing a stuck batch from device logs
      // alone (confirmed painful while investigating a headless engine that
      // reported a batch over threshold but never uploaded it). console.error
      // here shows up in logcat via FolderSyncForegroundService's
      // onConsoleMessage capture for the headless case, and in a normal
      // browser console otherwise.
      console.error('useLocalAutoSync: resolveAndSubmit bailed', {
        storageKey, entries: entriesToSubmit.length, busy: busyRef.current, activeElsewhere: activeSubmissions.has(storageKey),
      });
      return;
    }
    console.log('[AutoSync] resolveAndSubmit: storageKey=%s starting submission of %d file(s)', storageKey, entriesToSubmit.length, entriesToSubmit.map((e) => e.relativePath));
    busyRef.current = true;
    activeSubmissions.add(storageKey);
    // Move out of pendingQueue and persist immediately, before calling onBatchReady -
    // so if the page closes/refreshes while onBatchReady is still awaiting (e.g. an
    // EC2-ASG instance booting for minutes), the next mount finds these in
    // inFlightBatch rather than pendingQueue and reattaches instead of resending.
    const submittedPaths = new Set(entriesToSubmit.map((e) => e.relativePath));
    pendingQueueRef.current = pendingQueueRef.current.filter((e) => !submittedPaths.has(e.relativePath));
    inFlightBatchRef.current = entriesToSubmit;
    setState((s) => ({ ...s, status: 'submitting', pendingCount: pendingQueueRef.current.length }));
    // This batch is now spoken for (uploading, tracked by its own native job
    // once handleAutoSyncBatch hands it off) - shrink native's own pending
    // mirror to match so AutoSyncCheckWorker never double-queues it later.
    // Awaited, and before onBatchReady is called below, so the mirror is
    // guaranteed updated before any upload work (and hence before
    // UploadJobStore.hasActiveJobForEvent's own registration) actually begins.
    await nativeSyncService.setAutoSyncPending(storageKey, pendingQueueRef.current.map((e) => e.relativePath));
    notify(`Uploading ${entriesToSubmit.length} photo${entriesToSubmit.length === 1 ? '' : 's'}…`);
    await persist();
    try {
      // Lazy descriptors, not eagerly-resolved Files: onBatchReady's upload
      // engine (runOrganiserUploadBatches) resolves each one
      // from SAF right before compressing it, one at a time, instead of this
      // loop copying every original in the batch into app cache up front -
      // confirmed on a real device that resolving a 100+ file batch eagerly
      // (before any compression started) caused large, unbounded app storage
      // growth. `resolveFile` itself still drops a moved/deleted source file
      // silently (already marked "seen", won't be re-queued) - see the
      // matching try/catch in each upload engine's per-file loop.
      const resolved = entriesToSubmit.map((entry) => ({
        relativePath: entry.relativePath,
        resolveFile: () => provider.resolveFile(dirHandleRef.current, entry.relativePath),
      }));
      // `onProgress({phase: 'compressing'|'uploading', completed, total})` lets
      // a JS-driven onBatchReady (the web/PWA path only - the native branch
      // hands off to a real WorkManager job, which already gets this via
      // CmUploadWorker's own UploadNotifications.updateProgress calls) surface
      // the same "N / M photos compressed"/"uploaded" notification wording
      // organiser uploads already show natively, instead of the persistent
      // notification sitting on "N pending" for the whole batch with no
      // further feedback until it resolves or fails. Routed through
      // reportNativeBatchResult (not notify()) - see that function's own doc
      // comment for why notify() itself never actually reaches the user here.
      const onProgress = (progress) => {
        reportNativeBatchResult('submitting', `${progress.completed} / ${progress.total} photos ${progress.phase === 'compressing' ? 'compressed' : 'uploaded'}`);
      };
      // Passed through so a native job can persist the folder's treeUri into
      // its manifest (see stagingProvider.createJob's resumeSource) - lets a
      // resume re-resolve any not-yet-compressed file straight from the SAF
      // folder instead of only recovering files that were already staged.
      await onBatchReadyRef.current(resolved, dirHandleRef.current?.treeUri, onProgress);
      console.log('[AutoSync] resolveAndSubmit: storageKey=%s submission succeeded for %d file(s)', storageKey, entriesToSubmit.length);
      inFlightBatchRef.current = [];
      setState((s) => ({ ...s, status: 'watching', lastError: '' }));
      notify(describeSyncStatus({
        folderName: dirHandleRef.current?.name || 'folder',
        pendingCount: pendingQueueRef.current.length,
        batchSize: batchSizeRef.current,
        checking: false,
      }));
      reportNativeBatchResult('success');
      await persist();
    } catch (err) {
      console.error('useLocalAutoSync: resolveAndSubmit failed, requeuing', storageKey, err?.message || err);
      // Put the entries back at the front of pendingQueue so the next threshold
      // hit or manual "Sync now" retries the same batch.
      pendingQueueRef.current = [...inFlightBatchRef.current, ...pendingQueueRef.current];
      inFlightBatchRef.current = [];
      nativeSyncService.setAutoSyncPending(storageKey, pendingQueueRef.current.map((e) => e.relativePath));
      // A 409 with retryable:true from initiate-grouping means another session
      // (the organiser, or another concurrent upload-link visitor) currently
      // holds the one-job-per-event lock - not a real failure. (Some other 409s,
      // e.g. "existing gallery metadata is required before adding photos", are
      // permanent business-rule violations retrying would never resolve, so the
      // status code alone isn't a safe signal - see the retryable flag on the
      // matching backend responses in grouping.py/event_management.py.) The
      // batch stays queued as usual, but rather than only retrying once
      // pendingQueue crosses batchSize again (which may never happen if uploads
      // trickle in slowly), schedule a dedicated short retry so this batch keeps
      // trying to acquire the lock on its own cadence.
      const isLockBusy = Boolean(err?.data?.retryable);
      setState((s) => ({
        ...s,
        status: 'watching',
        pendingCount: pendingQueueRef.current.length,
        lastError: isLockBusy
          ? 'Another upload is grouping right now - yours is queued and will retry automatically.'
          : (err?.message || 'Sync failed - will retry.'),
      }));
      if (isLockBusy) {
        notify('Another upload is grouping right now - queued, will retry automatically.');
      } else {
        notify(describeSyncStatus({
          folderName: dirHandleRef.current?.name || 'folder',
          pendingCount: pendingQueueRef.current.length,
          batchSize: batchSizeRef.current,
          checking: false,
        }));
      }
      reportNativeBatchResult(isLockBusy ? 'queued' : 'failed');
      await persist();
      if (isLockBusy && !lockBusyTimerRef.current) {
        lockBusyTimerRef.current = setTimeout(function retry() {
          lockBusyTimerRef.current = null;
          if (!pendingQueueRef.current.length) return;
          if (isExternallyBusyRef.current?.()) {
            // Still can't start a new cycle right now (e.g. a separate manual
            // upload is mid-flight) - keep retrying on the same cadence
            // instead of dropping this batch's dedicated retry entirely.
            lockBusyTimerRef.current = setTimeout(retry, LOCK_BUSY_RETRY_MS);
            return;
          }
          resolveAndSubmit(pendingQueueRef.current.slice(0, Math.min(pendingQueueRef.current.length, AUTO_SYNC_MAX_BATCH_SIZE)));
        }, LOCK_BUSY_RETRY_MS);
      }
    } finally {
      busyRef.current = false;
      activeSubmissions.delete(storageKey);
    }
  };

  const doPoll = async () => {
    if (!dirHandleRef.current || busyRef.current || scanningRef.current) {
      console.log('[AutoSync] doPoll: skipped (armed=%s busy=%s scanning=%s)', Boolean(dirHandleRef.current), busyRef.current, scanningRef.current);
      return;
    }
    console.log('[AutoSync] doPoll: tick start, storageKey=%s pendingCount=%d', storageKey, pendingQueueRef.current.length);
    busyRef.current = true;
    scanningRef.current = true;
    const controller = new AbortController();
    scanAbortRef.current = controller;
    let scanError = null;
    let needsReconnectNow = false;
    try {
      const perm = await provider.queryPermission(dirHandleRef.current);
      if (perm !== 'granted') {
        needsReconnectNow = true;
        stopPolling();
        needsReconnectRef.current = true;
        setState((s) => ({ ...s, needsReconnect: true, status: 'needs-reconnect' }));
        persist();
        return;
      }
      const folderName = dirHandleRef.current.name || 'folder';
      // The heartbeat: this is the one point in every 25s cycle that proves to
      // the user (via the OS notification, which survives the tab being
      // backgrounded/minimised) that auto-sync hasn't silently died - the scan
      // itself now finishes too fast to show real progress, so this is a
      // deliberate "still alive" signal rather than a progress readout.
      notify(describeSyncStatus({ folderName, pendingCount: pendingQueueRef.current.length, batchSize: batchSizeRef.current, checking: true }));
      setState((s) => ({ ...s, scanning: true, scanProgress: { phase: 'scanning', completed: 0, total: null } }));
      const entries = await provider.listImageFiles(dirHandleRef.current, {
        onProgress: (p) => setState((s) => ({ ...s, scanProgress: p })),
        signal: controller.signal,
      });
      const newOnes = entries.filter((e) => !seenKeysRef.current.has(e.relativePath));
      console.log('[AutoSync] doPoll: storageKey=%s scanned %d file(s) in folder, %d new (seenKeys=%d)', storageKey, entries.length, newOnes.length, seenKeysRef.current.size);
      if (newOnes.length) {
        newOnes.forEach((e) => seenKeysRef.current.add(e.relativePath));
        pendingQueueRef.current = [
          ...pendingQueueRef.current,
          ...newOnes.map((e) => ({ relativePath: e.relativePath })),
        ];
        setState((s) => ({ ...s, pendingCount: pendingQueueRef.current.length }));
        // Keeps AutoSyncCheckWorker's native seen-set (the closed-app
        // fallback) in sync with this hook's own authoritative seenKeys, so
        // it never re-detects (and re-uploads) something this poll already
        // found - see AutoSyncSeenStore's own doc comment.
        // Awaited (unlike the fire-and-forget calls elsewhere in this file) so
        // AutoSyncCheckWorker's own native seen-set is guaranteed updated before
        // this function returns and a threshold-triggered resolveAndSubmit (below)
        // starts the actual upload - without this, the closed-app worker could
        // still read a stale seen-set in the gap and re-detect these same files
        // as new on its own next tick. See AutoSyncSeenStore's doc comment.
        await nativeSyncService.markAutoSyncSeen(storageKey, newOnes.map((e) => e.relativePath));
        await nativeSyncService.setAutoSyncPending(storageKey, pendingQueueRef.current.map((e) => e.relativePath));
        // reportNativeBatchResult was previously only called from
        // resolveAndSubmit (i.e. once a batch actually reaches the submission
        // threshold) - on native this left FolderSyncForegroundService's own
        // reportedPendingCount (and hence its OS notification text) stuck at
        // whatever it was after the last submission, showing "Up to date"
        // even after this scan just found new pending photos below threshold.
        // Confirmed on a real device: 1 new photo detected, notification kept
        // saying "Up to date" instead of "1 photo pending".
        //
        // Skipped entirely when we're about to immediately call
        // resolveAndSubmit below in this same tick: 'watching' is a
        // terminal-shaped signal on the native side (clears headlessBusy,
        // tears the headless WebView down once idle - see
        // FolderSyncForegroundService's ACTION_REPORT_RESULT/onBatchResult
        // handling) and would destroy the WebView before the real
        // compress/upload work even starts. Confirmed on a real device as
        // the root cause of a headless batch that correctly detected and
        // reported crossing its threshold, then silently never uploaded
        // anything - this exact report tore the WebView down out from under
        // resolveAndSubmit a moment later. resolveAndSubmit's own
        // 'submitting'/'success'/'failed' reports take over from here instead
        // whenever we ARE about to submit.
        const aboutToSubmit = pendingQueueRef.current.length >= batchSizeRef.current && !isExternallyBusyRef.current?.();
        console.log('[AutoSync] doPoll: storageKey=%s pendingCount=%d/%d threshold aboutToSubmit=%s externallyBusy=%s', storageKey, pendingQueueRef.current.length, batchSizeRef.current, aboutToSubmit, Boolean(isExternallyBusyRef.current?.()));
        if (!aboutToSubmit) {
          reportNativeBatchResult('watching');
        }
        await persist();
      }
    } catch (err) {
      if (err instanceof ScanCancelledError) {
        // User cancelled this cycle's scan - just skip it, the next poll tick
        // (or manual "Sync now") tries again from scratch.
        return;
      }
      scanError = err;
      setState((s) => ({ ...s, lastError: err?.message || 'Folder scan failed.' }));
    } finally {
      scanAbortRef.current = null;
      scanningRef.current = false;
      busyRef.current = false;
      setState((s) => ({ ...s, scanning: false, scanProgress: null, scanCancelRequested: false }));
      // Settle the heartbeat back from "checking" to the real state - skipped
      // on a permission loss, since dirHandleRef is still set there (kept
      // around for "Reconnect") but nothing was actually just checked.
      if (!needsReconnectNow) {
        notify(describeSyncStatus({
          folderName: dirHandleRef.current?.name || 'folder',
          pendingCount: pendingQueueRef.current.length,
          batchSize: batchSizeRef.current,
          checking: false,
        }));
      }
    }
    // If something else the caller cares about is busy (e.g. a separate manual
    // upload pipeline, or grouping already active from any origin), leave the
    // batch queued rather than starting a new cycle - the next poll tick will
    // re-check once it's clear, so this just waits instead of contending.
    if (!scanError && pendingQueueRef.current.length >= batchSizeRef.current && !isExternallyBusyRef.current?.()) {
      // batchSizeRef is only the THRESHOLD for "enough new photos to bother
      // syncing" - the actual submission is capped separately at
      // AUTO_SYNC_MAX_BATCH_SIZE so a huge backlog becomes one (or a couple)
      // large jobs instead of fragmenting into dozens of batchSizeRef-sized ones.
      await resolveAndSubmit(pendingQueueRef.current.slice(0, Math.min(pendingQueueRef.current.length, AUTO_SYNC_MAX_BATCH_SIZE)));
    }
  };
  const doPollRef = useRef(doPoll);
  doPollRef.current = doPoll;

  // `skipImmediate` lets a caller that just did the equivalent of a poll's own
  // scan itself (selectFolder, on first arm) start the recurring timer
  // without doPoll immediately re-walking the same folder a second time.
  const startPolling = (skipImmediate) => {
    stopPolling();
    if (!skipImmediate) doPollRef.current();
    pollTimerRef.current = setInterval(() => doPollRef.current(), POLL_INTERVAL_MS);
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (lockBusyTimerRef.current) {
      clearTimeout(lockBusyTimerRef.current);
      lockBusyTimerRef.current = null;
    }
  };

  // Resume on mount: if this event has an armed auto-sync folder persisted
  // from a previous visit, silently reattach without asking the user to
  // re-pick the folder (only a permission re-grant, if the browser revoked it).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const rec = await getFolderSyncState(eventId, storageKey).catch(() => null);
      if (cancelled) return;
      if (rec && rec.mode === 'autosync' && rec.armed && rec.dirHandle) {
        dirHandleRef.current = rec.dirHandle;
        seenKeysRef.current = new Set((rec.seenKeys || []).map(normalizeSeenKey));
        pendingQueueRef.current = rec.pendingQueue || [];
        inFlightBatchRef.current = rec.inFlightBatch || [];
        batchSizeRef.current = rec.batchSizeSetting || DEFAULT_BATCH_SIZE;
        const folderName = rec.dirHandle.name || 'folder';
        const perm = await provider.queryPermission(rec.dirHandle);
        if (perm === 'granted') {
          // Reconcile with whatever AutoSyncCheckWorker/AutoSyncTrigger (the closed-app
          // native fallbacks) did on their own while this JS engine wasn't running at all -
          // they only ever write to their own native AutoSyncSeenStore, never to this
          // hook's IndexedDB seenKeys/pendingQueue. Two separate gaps, both closed here:
          //   1. Files native has SEEN (discovered, whether or not it's uploaded them yet)
          //      must be merged into our own seenKeysRef - otherwise our next scan below
          //      treats them as brand new and re-queues them, even ones native has *already
          //      fully uploaded*. Confirmed on a real device: reopening the app while a
          //      later native-triggered batch was still processing made this hook re-upload
          //      13 files a second time, because its own seenKeys had never heard of them.
          //   2. Files native has queued but not yet started (a subset of #1) must be
          //      merged into our own pendingQueue, or they'd stay invisible to our own
          //      threshold check even though the persistent notification already correctly
          //      counts them as pending.
          const [nativeSeen, nativePending] = await Promise.all([
            nativeSyncService.getAutoSyncSeen(storageKey),
            nativeSyncService.getAutoSyncPending(storageKey),
          ]);
          if (nativeSeen.length || nativePending.length) {
            const nativeSeenSet = new Set(nativeSeen);
            const nativePendingSet = new Set(nativePending);
            nativeSeen.forEach((p) => seenKeysRef.current.add(p));
            // Drop anything from OUR OWN pendingQueue that native has seen but is no longer
            // holding pending itself - i.e. native has already started/finished uploading it
            // under our nose. Deliberately checked against nativeSeenSet specifically, not
            // seenKeysRef as a whole (which now also includes our own prior seenKeys) - every
            // pendingQueue entry is always in seenKeys too under normal operation (that's just
            // what "seen" means: discovered, not necessarily processed), so testing against
            // the merged seenKeysRef here would wrongly strip out perfectly normal, still-
            // legitimately-pending entries this hook queued itself before native ever touched
            // them. A file still in native's own pending list is kept (and topped up below)
            // since it's genuinely still awaiting upload, just not started by us.
            pendingQueueRef.current = pendingQueueRef.current.filter(
              (e) => !(nativeSeenSet.has(e.relativePath) && !nativePendingSet.has(e.relativePath)),
            );
            const alreadyKnown = new Set([
              ...pendingQueueRef.current.map((e) => e.relativePath),
              ...inFlightBatchRef.current.map((e) => e.relativePath),
            ]);
            const newlyDiscovered = nativePending.filter((p) => !alreadyKnown.has(p));
            if (newlyDiscovered.length) {
              pendingQueueRef.current = [
                ...pendingQueueRef.current,
                ...newlyDiscovered.map((relativePath) => ({ relativePath })),
              ];
            }
            await persist();
          }
          needsReconnectRef.current = false;
          setState((s) => ({
            ...s, loaded: true, armed: true, needsReconnect: false, folderName,
            batchSizeSetting: batchSizeRef.current, pendingCount: pendingQueueRef.current.length, status: 'watching',
          }));
          await persist();
          if (inFlightBatchRef.current.length) {
            // A batch was mid-submission when this event page was last closed.
            // Reattach to whatever the server actually did instead of blindly
            // resending (which would race a still-running job) or blindly
            // discarding (which would silently drop photos on a real failure).
            setState((s) => ({ ...s, status: 'submitting' }));
            let reattachFailed = false;
            try {
              await onReattachRef.current?.();
              inFlightBatchRef.current = [];
            } catch {
              pendingQueueRef.current = [...inFlightBatchRef.current, ...pendingQueueRef.current];
              inFlightBatchRef.current = [];
              reattachFailed = true;
            }
            if (cancelled) return;
            setState((s) => ({ ...s, status: 'watching', pendingCount: pendingQueueRef.current.length }));
            await persist();
            // onReattach (e.g. EventPage's reattachIfJobInFlight) can itself await
            // waitForGroupingCompletion - i.e. this whole branch can still be resolving
            // minutes (or, on a stuck server-side job, tens of minutes) after the page
            // first mounted. The batch's own original resolveAndSubmit call never got to
            // report back (the page was closed/killed mid-wait), so without this the
            // native persistent notification stays frozen at whatever stale count it
            // showed right before the interruption, even though this reattach just
            // correctly resolved it one way or the other - confirmed on a real device as
            // a notification stuck reporting N pending long after that exact batch had
            // already finished uploading and grouping successfully.
            //
            // Skipped entirely when the requeued entries (for upload-link's
            // onReattach, this is EVERY reattach - see requeueOnUploadLinkReattach's
            // own doc comment) are about to immediately go through startPolling's
            // doPoll -> resolveAndSubmit call below, in this same wake: 'queued' is
            // just as terminal-shaped on the native side as 'watching' (clears
            // headlessBusy, tears the headless WebView down once idle), and would
            // destroy the WebView before that follow-up resolveAndSubmit call even
            // starts. Confirmed on a real device as an infinite loop: every headless
            // wake found the previous wake's own requeued batch still sitting there,
            // reported 'queued', got torn down by that report before its own retry
            // could run, leaving the exact same stuck batch for the next wake to find
            // and repeat - "N/M pending" frozen forever with no upload ever
            // completing, resolved only by opening the visible app (whose own
            // WebView isn't subject to this same teardown). resolveAndSubmit's own
            // 'submitting'/'success'/'failed' reports take over from here instead
            // whenever we're about to retry immediately.
            const willRetryImmediately = reattachFailed
              && pendingQueueRef.current.length >= batchSizeRef.current && !isExternallyBusyRef.current?.();
            if (!willRetryImmediately) {
              notify(describeSyncStatus({
                folderName: dirHandleRef.current?.name || folderName,
                pendingCount: pendingQueueRef.current.length,
                batchSize: batchSizeRef.current,
                checking: false,
              }));
              reportNativeBatchResult(reattachFailed ? 'queued' : 'success');
            }
          }
          startNativeService();
          startPolling();
        } else {
          needsReconnectRef.current = true;
          setState((s) => ({
            ...s, loaded: true, armed: true, needsReconnect: true, folderName,
            batchSizeSetting: batchSizeRef.current, pendingCount: pendingQueueRef.current.length, status: 'needs-reconnect',
          }));
          await persist();
        }
      } else {
        setState((s) => ({ ...s, loaded: true }));
      }
    })();
    return () => { cancelled = true; stopPolling(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, storageKey]);

  // Catch up immediately when the tab regains focus instead of waiting out
  // the rest of the poll interval.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && dirHandleRef.current && !state.needsReconnect) {
        doPollRef.current();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [state.needsReconnect]);

  // Re-push nativeTargetMeta to native whenever its meaningful fields actually
  // change (e.g. an anonymous visitor logs in after already arming auto-sync,
  // flipping canGroup from false to true) - startNativeService() itself
  // no-ops if nothing is armed yet, so this is safe to fire on every relevant
  // change regardless of arm state. Native treats a start call for an
  // already-running storageKey as just refreshing its parameters, same
  // idempotency every other reattach/setBatchSizeSetting call already relies on.
  useEffect(() => {
    startNativeService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nativeTargetMeta?.uploadLinkToken, nativeTargetMeta?.identity?.name, nativeTargetMeta?.identity?.email,
    nativeTargetMeta?.identity?.phone, nativeTargetMeta?.sessionId, nativeTargetMeta?.canGroup, nativeTargetMeta?.groupingMode,
  ]);

  // `resolveIncludeExisting()` is asked right after the folder is picked
  // (permission granted) and before the - potentially slow, for a huge
  // folder - scan runs, so the user isn't left staring at a progress bar
  // before even being asked what it's for. It should return true to queue
  // whatever existing photos turn up for the first batch, false to mark them
  // as already-seen (only photos added from now on are picked up), or
  // null/undefined to cancel folder setup entirely (skipping the scan). If
  // omitted, existing photos are included by default. Asked unconditionally -
  // since we haven't scanned yet, we can't know whether the folder is empty.
  //
  // `resolveStopOtherAutoSync(other)`, if given, is asked only when another
  // auto-sync is already armed elsewhere on this device (any event/link,
  // organiser or upload-link) - see autoSyncRegistry.js's
  // findOtherArmedAutoSync. Should return true to stop that one and proceed
  // with arming this folder, or false/undefined to cancel this arm attempt
  // entirely and leave the existing one running. Only one auto-sync may run
  // at a time device-wide - see stopAutoSyncRemote's own doc comment for why.
  const selectFolder = async (resolveIncludeExisting, resolveStopOtherAutoSync) => {
    // A previous pick/scan (or a background poll) is still in flight - ignore
    // this click instead of opening a second OS directory picker on top of it.
    if (scanningRef.current) return;
    const handle = await provider.pickDirectory();
    if (!handle) {
      console.log('[AutoSync] selectFolder: storageKey=%s user cancelled the folder picker', storageKey);
      return; // user cancelled the OS picker
    }
    console.log('[AutoSync] selectFolder: storageKey=%s folder picked, name=%s treeUri=%s', storageKey, handle.name, handle.treeUri);

    const other = await findOtherArmedAutoSync(storageKey);
    if (other) {
      const shouldStop = await resolveStopOtherAutoSync?.(other);
      if (!shouldStop) return; // user declined - leave the existing auto-sync running, cancel this one
      await stopAutoSyncRemote(other.eventId, other.storageKey);
    }

    let includeExisting = true;
    if (resolveIncludeExisting) {
      includeExisting = await resolveIncludeExisting();
      if (includeExisting === null || includeExisting === undefined) return; // user cancelled
    }

    scanningRef.current = true;
    const controller = new AbortController();
    scanAbortRef.current = controller;
    setState((s) => ({ ...s, scanning: true, scanProgress: { phase: 'scanning', completed: 0, total: null } }));

    let entries = [];
    let wasCancelled = false;
    // No OS notification during this initial scan - it's a one-off, and the
    // walk is fast enough now (no per-file reads) that there'd rarely be
    // anything meaningful to show before it's already done. The persistent
    // "auto-sync is alive" notification starts once armed, below.
    try {
      entries = await provider.listImageFiles(handle, {
        onProgress: (p) => setState((s) => ({ ...s, scanProgress: p })),
        signal: controller.signal,
      });
    } catch (err) {
      if (err instanceof ScanCancelledError) {
        wasCancelled = true;
      }
      // Any other failure: proceed as if the folder were empty - the next
      // poll will surface a real scan error to the user instead.
    } finally {
      scanAbortRef.current = null;
      scanningRef.current = false;
      setState((s) => ({ ...s, scanning: false, scanProgress: null, scanCancelRequested: false }));
    }
    if (wasCancelled) return;

    dirHandleRef.current = handle;
    inFlightBatchRef.current = [];
    // Starting from an empty seenKeys, every entry just found is necessarily
    // "new" - so this reuses the exact same new-vs-seen logic doPoll runs on
    // every later scan, applied to the entries we already have in hand,
    // instead of arming with an empty queue and immediately triggering a
    // second full walk of the same folder via startPolling's own first tick
    // just to discover the same files again.
    seenKeysRef.current = includeExisting ? new Set() : new Set(entries.map((e) => e.relativePath));
    const newOnes = entries.filter((e) => !seenKeysRef.current.has(e.relativePath));
    newOnes.forEach((e) => seenKeysRef.current.add(e.relativePath));
    pendingQueueRef.current = newOnes.map((e) => ({ relativePath: e.relativePath }));
    // Bulk one-time seed of AutoSyncCheckWorker's native seen-set (the
    // closed-app fallback) to this freshly-computed baseline - without this,
    // its first-ever closed-app check would see every pre-existing file in
    // the folder as "new" and re-upload the whole thing. See
    // AutoSyncSeenStore's own doc comment.
    nativeSyncService.seedAutoSyncSeen(storageKey, Array.from(seenKeysRef.current));
    nativeSyncService.setAutoSyncPending(storageKey, pendingQueueRef.current.map((e) => e.relativePath));
    const folderName = handle.name || 'folder';
    needsReconnectRef.current = false;
    console.log('[AutoSync] selectFolder: storageKey=%s armed, folderName=%s initialScan=%d file(s), %d queued as pending (includeExisting=%s)', storageKey, folderName, entries.length, pendingQueueRef.current.length, includeExisting);
    setState((s) => ({
      ...s, armed: true, needsReconnect: false, folderName, pendingCount: pendingQueueRef.current.length, status: 'watching', lastError: '',
    }));
    await persist({ seenKeys: Array.from(seenKeysRef.current), pendingQueue: pendingQueueRef.current });
    // Starts the persistent "auto-sync is alive and watching" notification -
    // resolveAndSubmit's own "Uploading…" notify() immediately supersedes
    // this if a first batch fires right below, which is fine, that's the
    // more accurate thing to show at that moment.
    notify(describeSyncStatus({ folderName, pendingCount: pendingQueueRef.current.length, batchSize: batchSizeRef.current, checking: false }));
    reportNativeBatchResult('watching');
    if (pendingQueueRef.current.length >= batchSizeRef.current && !isExternallyBusyRef.current?.()) {
      // Same AUTO_SYNC_MAX_BATCH_SIZE cap as doPoll's trigger - matters most
      // right here: arming with "include existing photos" on a large folder is
      // exactly the case that used to fragment into dozens of small batches.
      await resolveAndSubmit(pendingQueueRef.current.slice(0, Math.min(pendingQueueRef.current.length, AUTO_SYNC_MAX_BATCH_SIZE)));
    }
    startNativeService();
    startPolling(true); // we already just scanned; skip doPoll's own immediate re-scan
  };

  const syncNow = async () => {
    if (!dirHandleRef.current || !pendingQueueRef.current.length || isExternallyBusyRef.current?.()) return;
    console.log('[AutoSync] syncNow: storageKey=%s manually triggered with %d pending file(s)', storageKey, pendingQueueRef.current.length);
    await resolveAndSubmit(pendingQueueRef.current.slice());
  };

  // Aborts an in-flight folder scan (initial pick or a background re-poll of
  // a huge folder) - the abort itself is asynchronous (the scan notices and
  // throws ScanCancelledError at its next chunk/entry checkpoint, then
  // selectFolder's/doPoll's own finally block clears `scanning`), so this just
  // flags cancellation was requested for the UI's cancel button to reflect.
  const cancelScan = () => {
    if (!scanAbortRef.current) return;
    setState((s) => ({ ...s, scanCancelRequested: true }));
    scanAbortRef.current.abort();
  };

  const reconnect = async () => {
    if (!dirHandleRef.current) return;
    const perm = await provider.requestPermission(dirHandleRef.current);
    if (perm === 'granted') {
      needsReconnectRef.current = false;
      setState((s) => ({ ...s, needsReconnect: false, status: 'watching', lastError: '' }));
      persist();
      startPolling();
    } else {
      needsReconnectRef.current = true;
      setState((s) => ({ ...s, needsReconnect: true, status: 'needs-reconnect', lastError: 'Folder access was not granted.' }));
      persist();
    }
  };

  // Lets a caller with its own "Cancel" affordance (e.g. AddPhotosSection's
  // progress UI, when the batch driving it came from auto-sync rather than
  // its own manual button) abandon whatever batch is currently in flight
  // instead of it silently getting requeued and retried the moment the
  // caller's own upload/grouping-wait promise chain next rejects. Without
  // this there was no way to actually stop a stuck auto-sync-triggered job -
  // only ever a way to hide/reset the *local* UI state around it, which the
  // engine itself never learned about, so the exact same batch would just
  // come back a few seconds later.
  const cancelInFlightBatch = () => {
    inFlightBatchRef.current = [];
    busyRef.current = false;
    setState((s) => ({ ...s, status: 'watching', lastError: '' }));
    persist();
  };

  const setBatchSizeSetting = (n) => {
    const value = Math.max(1, Math.min(2000, Number(n) || DEFAULT_BATCH_SIZE));
    batchSizeRef.current = value;
    setState((s) => ({ ...s, batchSizeSetting: value }));
    persist();
    // Without this, only this JS instance's own in-memory batchSizeRef (and
    // IndexedDB, via persist() above) ever learns about the new threshold -
    // native's own copy (SyncPrefs.Target.batchThreshold, read independently by
    // AutoSyncCheckWorker's closed-app fallback and unrelated to this hook's own
    // poll) stays stuck at whatever value was last passed to startForegroundSync
    // (arm time, or the last app-open reattach). Confirmed as the reason a
    // lowered threshold never triggered a closed-app upload: the pending count
    // had already crossed the new (lower) value the user just set, but
    // AutoSyncCheckWorker kept comparing it against the stale, still-higher
    // native threshold and never called startUploadForTarget. startNativeService
    // is safe to call again here - the native side treats a start call for an
    // already-running storageKey as just refreshing its parameters (same
    // idempotency this hook already relies on for every mount-reattach).
    startNativeService();
  };

  const stopSync = async () => {
    console.log('[AutoSync] stopSync: storageKey=%s', storageKey);
    stopPolling();
    stopNativeService();
    // A native upload job already handed off to CmUploadWorker (WorkManager)
    // runs entirely independent of this watcher/poller once dispatched - by
    // design, so it survives the app being closed (see nativeUploadService's
    // own header comment). Without this, "Stop auto-sync" only ever stopped
    // detecting/queuing NEW batches; an already-dispatched job (including one
    // stuck in its own retry loop) kept running in the background with no way
    // for the user to actually make it stop - confirmed on-device. At most one
    // native job can be active per event at a time (see
    // UploadJobStore.hasActiveJobForEvent on the native side), so this is
    // always cancelling "the" in-flight job for this event, never a manual
    // upload the user started separately for some other event. No-ops on
    // web/PWA (nativeUploadService's own platform guard).
    try {
      const { jobs } = await nativeUploadService.listActiveJobs();
      const eventIdStr = String(eventId);
      await Promise.all(
        (jobs || [])
          .filter((j) => String(j.eventId) === eventIdStr)
          .map((j) => nativeUploadService.cancelJob({ jobId: j.jobId })),
      );
    } catch (err) {
      console.warn('[AutoSync] stopSync: cancelling native job(s) failed (non-fatal)', storageKey, err?.message || err);
    }
    dirHandleRef.current = null;
    seenKeysRef.current = new Set();
    pendingQueueRef.current = [];
    inFlightBatchRef.current = [];
    needsReconnectRef.current = false;
    setState((s) => ({
      ...s, armed: false, needsReconnect: false, folderName: '', pendingCount: 0, status: 'idle', lastError: '',
    }));
    clearNotify();
    await deleteFolderSyncState(eventId, storageKey);
  };
  stopSyncRef.current = stopSync;

  // Auto-sync uploads as a specific identity - the signed-in organiser
  // (EventPage) or a guest's own name/email/session (UploadLinkPage's
  // nativeTargetMeta) - captured at arm time. Neither the live poll loop nor
  // the native foreground service/WorkManager re-derives that identity on
  // its own, so a login or logout happening while a folder is still armed
  // would otherwise silently keep uploading under the OLD identity - wrong,
  // and a real privacy problem on a device shared across accounts. Stopped
  // instead; the user re-arms (and re-consents) under whichever identity is
  // now active. `authKeyRef`'s initial `undefined` is a sentinel distinct
  // from `null` (the signed-out key) - the first run just records the
  // already-hydrated baseline (AuthProvider only renders children once its
  // own localStorage restore finishes, so this is never a false "just logged
  // in") rather than treating page load itself as a login. See
  // AuthContext.js's login/logout for the matching device-wide stop that
  // covers this same case when no page happens to be mounted at all.
  const authKeyRef = useRef(undefined);
  useEffect(() => {
    const key = user?.customer_id ?? null;
    if (authKeyRef.current === undefined) {
      authKeyRef.current = key;
      return;
    }
    if (authKeyRef.current !== key) {
      authKeyRef.current = key;
      stopSyncRef.current?.();
    }
  }, [user]);

  // Nuclear option for a suspected-corrupted local sync state (e.g. a stored
  // dirHandle that throws on every use, or anything stopSync/"Stop watching"
  // alone doesn't seem to fix) - wipes the *entire* local-folder-sync database,
  // not just this event/link's record. Every armed folder anywhere in the app
  // (this event or any other) will need to be re-selected afterwards, so the
  // caller should confirm with the user before calling this.
  const hardReset = async () => {
    stopPolling();
    stopNativeService();
    scanAbortRef.current?.abort();
    dirHandleRef.current = null;
    seenKeysRef.current = new Set();
    pendingQueueRef.current = [];
    inFlightBatchRef.current = [];
    scanningRef.current = false;
    needsReconnectRef.current = false;
    setState((s) => ({
      ...s,
      armed: false, needsReconnect: false, folderName: '', pendingCount: 0, status: 'idle', lastError: '',
      scanning: false, scanProgress: null, scanCancelRequested: false,
    }));
    clearNotify();
    await resetFolderSyncDb();
  };

  // Progress-reporting callbacks with the same shape runOrganiserUploadBatches
  // already expects (setLog/setProgress/setStatusComment), so
  // the caller of onBatchReady can just pass `{ setLog, setProgress, setStatusComment }`
  // straight through as the upload's `hooks` option.
  const setLog = (log) => setState((s) => ({ ...s, log }));
  const setProgress = (progress) => setState((s) => ({ ...s, progress }));
  const setStatusComment = (statusComment) => setState((s) => ({ ...s, statusComment }));

  return {
    ...state,
    selectFolder,
    syncNow,
    cancelScan,
    reconnect,
    setBatchSizeSetting,
    stopSync,
    hardReset,
    cancelInFlightBatch,
    setLog,
    setProgress,
    setStatusComment,
  };
}
